import React, { useState, useMemo } from 'react';

/**
 * Link input component that lets users choose between an external URL and an internal page.
 *
 * Props:
 * - value (string) — current link value (plain URL or 'page:slug' format)
 * - onChange(newValue) — callback with updated value
 * - pages (object) — pagesMeta map: { 'about': { title: 'about', order: 0 } }
 * - placeholder (string) — placeholder for URL input (default: 'https://...')
 */
const LinkInput = ({ value = '', onChange, pages = {}, placeholder = 'https://...' }) => {
  const isPageMode = value && value.startsWith('page:');
  const [mode, setMode] = useState(isPageMode ? 'page' : 'url');

  // Sort pages by order for the dropdown
  const sortedPages = useMemo(() => {
    return Object.entries(pages)
      .map(([slug, meta]) => ({ slug, title: meta.title || slug, order: meta.order ?? 999 }))
      .sort((a, b) => a.order - b.order);
  }, [pages]);

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
    // Clear value when switching modes
    onChange('');
  };

  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  const handlePageChange = (e) => {
    const selected = e.target.value;
    onChange(selected ? `page:${selected}` : '');
  };

  // Extract current page slug from value
  const currentPageSlug = isPageMode ? value.slice(5) : '';

  return (
    <div className="space-y-2">
      {/* Mode toggle */}
      <div className="flex rounded-lg overflow-hidden border border-gray-700 w-fit">
        <button
          type="button"
          onClick={() => handleModeSwitch('url')}
          className={`px-3 py-1 text-xs font-medium transition-colors ${
            mode === 'url'
              ? 'bg-[#749555] text-white'
              : 'bg-[#1a1a2e] text-gray-400 hover:text-white'
          }`}
        >
          URL
        </button>
        <button
          type="button"
          onClick={() => handleModeSwitch('page')}
          className={`px-3 py-1 text-xs font-medium transition-colors ${
            mode === 'page'
              ? 'bg-[#749555] text-white'
              : 'bg-[#1a1a2e] text-gray-400 hover:text-white'
          }`}
        >
          Page
        </button>
      </div>

      {/* Input */}
      {mode === 'url' ? (
        <input
          type="text"
          value={isPageMode ? '' : (value || '')}
          onChange={handleUrlChange}
          placeholder={placeholder}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      ) : (
        <select
          value={currentPageSlug}
          onChange={handlePageChange}
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white focus:border-[#749555] focus:outline-none"
        >
          <option value="">Select a page...</option>
          <option value="/">Home</option>
          {sortedPages.map((page) => (
            <option key={page.slug} value={page.slug}>
              {page.title}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export default LinkInput;
